/**
 * Standalone utility for processing incoming messages for decryption.
 *
 * Handles all wire formats:
 *   - New (ECDH): Base64(IV):Base64(ciphertext):Base64(signature)
 *   - Legacy (RSA): JSON { ciphertext, keyId, algorithm, ... }
 *   - Group: Base64(IV):Base64(ciphertext):Base64(signature)
 *   - Group key distribution: JSON { cls: "encrypted_group_key", group_name, content }
 *     (may also arrive ECDH-encrypted at the outer level — detected post-decryption)
 *   - Group key request: JSON { cls: "group_key_request", group_name }
 *
 * For ECDH messages, `peerUsername` must be provided so the peer's public
 * encryption key can be retrieved from IndexedDB for shared-secret derivation.
 * For group messages, `peerUsername` carries the group's routing address.
 * If it's missing, the message is shown as unreadable rather than silently dropped.
 */

import {
  isKeyExchangeMessage,
  parseKeyExchangeMessage,
  handleReceivedPublicKey,
} from "@/services/keyExchangeService";
import { getActiveKeySet, getPublicKeySetByUsername } from "@/services/keyStorageService";
import {
  decryptMessage,
  isEncryptedMessage,
  isEcdhWireFormat,
  parseEncryptedMessage,
  isGroupMessageWireFormat,
  decryptGroupMessage,
  authenticateGroupMessage,
} from "@/utils/crypto";
import {
  isGroupKeyDistributionMessage,
  parseGroupKeyDistributionMessage,
  receiveGroupKeyDistributionMessage,
  resolveGroupSymmetricKey,
  isGroupKeyRequestMessage,
} from "@/services/groupKeyService";
import { isPhotoMessage } from "@/utils/utils";

export interface ProcessedMessage {
  originalMessage: string;
  decryptedMessage: string;
  isEncrypted: boolean;
  isKeyExchange: boolean;
  isDecrypting: boolean;
  /** Set for group messages — the sender's username resolved from routing metadata. */
  senderUsername?: string;
  /**
   * Set when group message signature verification fails with the cached key.
   * Contains the sender's username so the middleware can fetch a fresh signing
   * key and reload (covers the Android multi-device re-login case).
   */
  needsKeyRefresh?: string;
  /**
   * Set when a group message can't be decrypted because our symmetric key is
   * missing or stale (AES-GCM tag mismatch). Contains the group routing address
   * so the middleware can request the current key from the owner and reload.
   * This is the self-healing path for group symmetric-key drift.
   */
  needsGroupKeyRefresh?: string;
  /**
   * The address key material was stored for (peer username for @keys, canonical group
   * address for a distribution) — unset when the event carried nothing usable. Scopes
   * the per-address retry gates via keyExchangeReceived.
   */
  keyArrivedFor?: string;
}

/**
 * Decrypt-failure placeholder strings, split retryable vs. permanent: retryable = the
 * missing key material can still arrive (counts toward `hasUndecrypted`, re-armed by the
 * per-address key version); permanent = the key was present and decrypt/verify still
 * failed, so no future fetch can fix it. Rationale: CLAUDE.md "hasUndecrypted classification".
 * The cache exact-matches messages against RETRYABLE_DECRYPT_PLACEHOLDERS.
 */
const DECRYPT_PLACEHOLDER = {
  // Retryable — waiting on key material that can still arrive.
  noPeerKey: "🔒 [Cannot decrypt — no key for this contact]",
  noLocalKey: "🔒 [Cannot decrypt — no local key]",
  groupKeyUnavailable: "🔒 [Cannot decrypt — group key not available]",
  // Permanent — had the key and it still failed, unrecoverable, or a context bug.
  decryptFailed: "[Encrypted Content]", // had own+peer keys; ECDH/AES decrypt threw
  groupDecryptFailed: "🔒 [Failed to decrypt group message]", // had a group key; AES-GCM tag mismatch
  groupAuthFailed: "🔒 [Group message authentication failed]",
  previousKey: "🔒 [Message encrypted for a previous key — message unrecoverable]",
  legacyRsa: "🔒 [Legacy encrypted message — key not available]",
  missingGroupAddress: "🔒 [Cannot decrypt group message — missing group address]",
  missingPeerKeyContext: "🔒 [Cannot decrypt — missing peer key context]",
} as const;

export const RETRYABLE_DECRYPT_PLACEHOLDERS: ReadonlySet<string> = new Set([
  DECRYPT_PLACEHOLDER.noPeerKey,
  DECRYPT_PLACEHOLDER.noLocalKey,
  DECRYPT_PLACEHOLDER.groupKeyUnavailable,
]);

/**
 * Processes a raw message string: handles key exchange, ECDH decryption,
 * legacy RSA decryption, group message decryption, group key distribution,
 * group key requests, and plaintext pass-through.
 *
 * @param message        - Raw message content
 * @param userId         - Current user's ID (for own key lookup)
 * @param peerUsername   - Address/username of the other party (required for ECDH decrypt).
 *                         For group messages this is the group's routing address.
 * @param senderUsername - Sender's username resolved from message metadata (used for
 *                         3-part Android group messages where it isn't embedded in the cipher)
 */
export async function processIncomingMessage(
  message: string,
  userId: number,
  peerUsername?: string,
  senderUsername?: string,
  keysVersion?: number,
  isHistory = false,
): Promise<ProcessedMessage> {
  const result: ProcessedMessage = {
    originalMessage: message,
    decryptedMessage: message,
    isEncrypted: false,
    isKeyExchange: false,
    isDecrypting: false,
  };

  // File/media messages (JSON with cls:"mms") are never encrypted — pass them through
  // before any wire-format sniffing. Without this, every attachment's multi-megabyte
  // base64 JSON gets JSON.parse'd and scanned repeatedly by the detectors below, which
  // dominates the load time of attachment-heavy conversations.
  if (isPhotoMessage(message)) {
    if (senderUsername) result.senderUsername = senderUsername;
    return result;
  }

  // Group key distribution message (arrives as a 1:1 message from the group owner)
  if (isGroupKeyDistributionMessage(message)) {
    result.isKeyExchange = true;
    result.decryptedMessage = "🔑 Group key received";
    // Skip in history — key was already stored when the message first arrived live.
    // Re-attempting the ECDH decrypt on every conversation open is expensive and
    // produces spurious errors if the web's own key has since changed.
    if (!isHistory) {
      const parsed = parseGroupKeyDistributionMessage(message);
      if (parsed && peerUsername) {
        try {
          const storedAt = await receiveGroupKeyDistributionMessage(parsed, userId, peerUsername);
          if (storedAt) {
            result.keyArrivedFor = storedAt;
            console.log(`[E2EE] Stored group key for "${parsed.group_name}" from "${peerUsername}"`);
          } else {
            console.warn(`[E2EE] Group key from "${peerUsername}" for "${parsed.group_name}" NOT stored — see [GroupKey] guard log above`);
          }
        } catch (err) {
          console.error("[E2EE] Failed to store group key:", err);
        }
      }
    }
    return result;
  }

  // Group key request — a non-owner member asking the owner for the symmetric key.
  // The middleware (websocketMiddleware) handles the owner auto-response for live
  // messages. Here we just mark it as a key exchange event so keyExchangeVersion
  // increments and checkEncryption re-runs.
  if (isGroupKeyRequestMessage(message)) {
    result.isKeyExchange = true;
    result.decryptedMessage = "🔑 Group key requested";
    return result;
  }

  // Detect whether peerUsername is a group routing address (JSON with gid field).
  // Used to identify 3-part Android group messages that look like ECDH otherwise.
  const isGroupContext = (() => {
    if (!peerUsername) return false;
    try { const p = JSON.parse(peerUsername); return typeof p?.gid !== "undefined"; }
    catch { return false; }
  })();

  // Group message: 3-part format, or legacy 4-part in a group conversation.
  if (isGroupMessageWireFormat(message, isGroupContext)) {
    result.isEncrypted = true;
    result.isDecrypting = true;
    const groupAddress = peerUsername;
    // Surface the sender up front (from the caller-resolved param) so the username renders
    // on a live "[Failed to decrypt]" / "[auth failed]" bubble too — not only after a refresh.
    // The success path below overrides this with the embedded/effective sender.
    if (senderUsername) result.senderUsername = senderUsername;
    try {
      if (!groupAddress) {
        result.decryptedMessage = DECRYPT_PLACEHOLDER.missingGroupAddress;
        return result;
      }
      const symmetricKeyB64 = await resolveGroupSymmetricKey(groupAddress);
      if (!symmetricKeyB64) {
        result.decryptedMessage = DECRYPT_PLACEHOLDER.groupKeyUnavailable;
        if (!isHistory) result.needsGroupKeyRefresh = groupAddress;
        return result;
      }
      const { plaintext, senderUsername: embeddedSender } = await decryptGroupMessage(message, symmetricKeyB64);
      // Legacy 4-part messages embed the sender in the cipher; current 3-part format
      // relies on the caller passing it via the senderUsername parameter.
      const effectiveSender = embeddedSender ?? senderUsername;

      // Verify whenever we hold the sender's key. Do NOT gate on keysVersion: for group
      // messages it's the symmetric-key version, unrelated to the sender's signing-key
      // timestamp — gating on it skipped verification for every @keys-sourced sender. A
      // genuinely stale key fails auth below, and needsKeyRefresh re-fetches + re-verifies.
      if (effectiveSender) {
        const senderKeys = await getPublicKeySetByUsername(effectiveSender);
        if (senderKeys) {
          const isAuthentic = await authenticateGroupMessage(message, senderKeys.signPublicKeyJwk);
          if (!isAuthentic) {
            result.decryptedMessage = DECRYPT_PLACEHOLDER.groupAuthFailed;
            // Stale signing key — flag the sender so the caller (live path + history) fetches
            // a fresh key and re-verifies, instead of waiting for a new live message.
            result.needsKeyRefresh = effectiveSender;
            if (!isHistory) {
              console.warn("[E2EE] Group message auth failed for", effectiveSender, "— signing key may be stale (new device login?)");
            }
            return result;
          }
        } else {
          // Have the group key but not this sender's signing key — show plaintext now and flag
          // the sender so the caller (live middleware / history two-pass) fetches @keys and re-verifies.
          result.needsKeyRefresh = effectiveSender;
          if (!isHistory) {
            console.warn("[E2EE] Group message from", effectiveSender, "shown unverified — fetching signing key to verify");
          }
        }
      }

      result.decryptedMessage = plaintext;
      result.senderUsername = effectiveSender ?? undefined;
    } catch (error) {
      result.decryptedMessage = DECRYPT_PLACEHOLDER.groupDecryptFailed;
      // AES-GCM tag mismatch — our symmetric key is stale relative to the sender's.
      // Signal the middleware to re-request the current key from the owner.
      if (!isHistory) {
        result.needsGroupKeyRefresh = groupAddress;
        console.error("[E2EE] Failed to decrypt group message:", error);
      }
    } finally {
      result.isDecrypting = false;
    }
    return result;
  }

  // Key exchange message from @keys relay
  if (isKeyExchangeMessage(message)) {
    result.isKeyExchange = true;
    result.decryptedMessage = "🔑 Public key received";
    const keyMsg = parseKeyExchangeMessage(message);
    if (keyMsg) {
      if (keyMsg.error) {
        console.warn("[E2EE] Key exchange error:", keyMsg.error, "for:", keyMsg.username);
      } else {
        console.log("[E2EE] Received public key for:", keyMsg.username);
        try {
          const stored = await handleReceivedPublicKey(keyMsg);
          // Stored successfully — a DM with this user can now retry its 🔒 rows.
          if (stored && keyMsg.username) result.keyArrivedFor = keyMsg.username;
        } catch (err) {
          console.error("[E2EE] Failed to store received public key:", err);
        }
      }
    }
    return result;
  }

  if (!isEncryptedMessage(message)) {
    if (senderUsername) result.senderUsername = senderUsername;
    return result;
  }

  result.isEncrypted = true;
  result.isDecrypting = true;

  try {
    if (isEcdhWireFormat(message)) {
      if (!peerUsername) {
        console.warn("[E2EE] ECDH message received but peerUsername not provided — cannot decrypt");
        result.decryptedMessage = DECRYPT_PLACEHOLDER.missingPeerKeyContext;
        return result;
      }

      const [ownKeySet, peerKeys] = await Promise.all([
        getActiveKeySet(userId),
        getPublicKeySetByUsername(peerUsername),
      ]);

      if (!ownKeySet) {
        result.decryptedMessage = DECRYPT_PLACEHOLDER.noLocalKey;
        return result;
      }

      if (!peerKeys) {
        result.decryptedMessage = DECRYPT_PLACEHOLDER.noPeerKey;
        return result;
      }

      // keysVersion = the version of OUR key the sender encrypted for (Android echoes the
      // timestamp_generated we published via @keys). If it != our current key's timestamp,
      // they used a stale copy → wrong ECDH secret → decrypt fails. Compare our own key,
      // not the peer's.
      if (
        keysVersion !== undefined &&
        keysVersion > 0 &&
        Math.floor(ownKeySet.createdAt / 1000) !== keysVersion
      ) {
        result.decryptedMessage = DECRYPT_PLACEHOLDER.previousKey;
        return result;
      }

      const plaintext = await decryptMessage(
        message,
        ownKeySet.encryptPrivateKeyJwk,
        peerKeys.encryptPublicKeyJwk
      );

      // A group key distribution message may arrive ECDH-encrypted at the outer
      // level. If so, handle it now rather than surfacing the raw JSON in the chat.
      if (isGroupKeyDistributionMessage(plaintext)) {
        result.isKeyExchange = true;
        result.decryptedMessage = "🔑 Group key received";
        if (!isHistory) {
          const parsed = parseGroupKeyDistributionMessage(plaintext);
          if (parsed && peerUsername) {
            try {
              const storedAt = await receiveGroupKeyDistributionMessage(parsed, userId, peerUsername);
              if (storedAt) result.keyArrivedFor = storedAt;
              console.log(`[E2EE] Stored group key for "${parsed.group_name}" from "${peerUsername}"`);
            } catch (err) {
              console.error("[E2EE] Failed to store group key (ECDH-wrapped):", err);
            }
          }
        }
        return result;
      }

      result.decryptedMessage = plaintext;
    } else {
      // Legacy RSA path — old messages from before the EC migration
      const encryptedMsg = parseEncryptedMessage(message);
      if (!encryptedMsg) throw new Error("Invalid legacy message format");

      // RSA private keys are cleared in the DB v2 upgrade — these messages
      // will show as unreadable, which is expected after the migration.
      result.decryptedMessage = DECRYPT_PLACEHOLDER.legacyRsa;
    }
  } catch (error) {
    if (!isHistory) console.error("[E2EE] Failed to decrypt message:", error);
    result.decryptedMessage = DECRYPT_PLACEHOLDER.decryptFailed;
  } finally {
    result.isDecrypting = false;
  }

  return result;
}
